import { useCallback, useMemo, useState } from "react";
import { type CellData, formatCellValue, getColumnHeader } from "./excel-parse";

/**
 * Column show/hide state for the ExcelViewer. Spreadsheets here can carry
 * dozens of columns, so the viewer lets the user prune them. Hidden columns
 * are tracked by their original index; `visibleColumnIndices` is the ordered
 * list of still-shown indices that the grid renders.
 */
export function useExcelColumnVisibility(
  columnCount: number,
  sheetData: CellData[][],
) {
  const [hiddenColumns, setHiddenColumns] = useState<Set<number>>(new Set());

  const reset = useCallback(() => setHiddenColumns(new Set()), []);

  const visibleColumnIndices = useMemo(
    () =>
      Array.from({ length: columnCount }, (_, i) => i).filter(
        (i) => !hiddenColumns.has(i),
      ),
    [columnCount, hiddenColumns],
  );

  const toggleColumn = useCallback((colIndex: number) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(colIndex)) next.delete(colIndex);
      else next.add(colIndex);
      return next;
    });
  }, []);

  const showAllColumns = useCallback(() => setHiddenColumns(new Set()), []);

  const hideAllColumns = useCallback(
    () =>
      setHiddenColumns(
        new Set(Array.from({ length: columnCount }, (_, i) => i)),
      ),
    [columnCount],
  );

  // Menu label: the header-row text when present, else the spreadsheet letter.
  const getColumnLabel = useCallback(
    (colIndex: number) => {
      const letter = getColumnHeader(colIndex);
      const headerText = formatCellValue(sheetData[0]?.[colIndex]).trim();
      return headerText ? `${letter} · ${headerText}` : letter;
    },
    [sheetData],
  );

  return {
    hiddenColumns,
    visibleColumnIndices,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    getColumnLabel,
    reset,
  };
}
